import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../services/workspaceGlossaryService';
import type {
  CreateWorkspaceGlossaryData,
  UpdateWorkspaceGlossaryData,
} from '../services/workspaceGlossaryService';

export const wsGlossaryKeys = {
  all: (workspaceId: string) => ['ws-glossary', workspaceId] as const,
  detail: (workspaceId: string, glossaryId: string) =>
    ['ws-glossary', workspaceId, glossaryId] as const,
  categories: (workspaceId: string) => ['ws-glossary-categories', workspaceId] as const,
};

export function useWorkspaceGlossary(
  workspaceId: string,
  params?: {
    q?: string;
    category?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  },
) {
  return useQuery({
    queryKey: [...wsGlossaryKeys.all(workspaceId), params],
    queryFn: () => api.fetchGlossaryEntries(workspaceId, params),
    enabled: !!workspaceId,
  });
}

export function useWorkspaceGlossaryEntry(workspaceId: string, glossaryId: string) {
  return useQuery({
    queryKey: wsGlossaryKeys.detail(workspaceId, glossaryId),
    queryFn: () => api.fetchGlossaryEntry(workspaceId, glossaryId),
    enabled: !!workspaceId && !!glossaryId,
  });
}

export function useGlossaryCategories(workspaceId: string) {
  return useQuery({
    queryKey: wsGlossaryKeys.categories(workspaceId),
    queryFn: () => api.fetchGlossaryCategories(workspaceId),
    enabled: !!workspaceId,
  });
}

export function useCreateWorkspaceGlossary(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkspaceGlossaryData) =>
      api.createGlossaryEntry(workspaceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wsGlossaryKeys.all(workspaceId) });
      queryClient.invalidateQueries({ queryKey: wsGlossaryKeys.categories(workspaceId) });
    },
  });
}

export function useUpdateWorkspaceGlossary(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { glossaryId: string; data: UpdateWorkspaceGlossaryData }) =>
      api.updateGlossaryEntry(workspaceId, params.glossaryId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: wsGlossaryKeys.detail(workspaceId, vars.glossaryId),
      });
      queryClient.invalidateQueries({ queryKey: wsGlossaryKeys.all(workspaceId) });
      queryClient.invalidateQueries({ queryKey: wsGlossaryKeys.categories(workspaceId) });
    },
  });
}

export function useDeleteWorkspaceGlossary(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (glossaryId: string) =>
      api.deleteGlossaryEntry(workspaceId, glossaryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wsGlossaryKeys.all(workspaceId) });
      queryClient.invalidateQueries({ queryKey: wsGlossaryKeys.categories(workspaceId) });
    },
  });
}
